/* MC-FIX 67 (2026-08-26): true JS-driven masonry for the collection/search
   product grid, replacing SITEWIDE-FIX-55's pure-CSS `columns: N` approach.

   WHY: FIX-55 (Round 29) used CSS multi-column layout (`columns: N`) to get
   Pinterest-style masonry. That works well for large catalogs, but CSS
   multi-column's default `column-fill: balance` algorithm does NOT treat
   `column-count` as a fixed number of columns -- it is a MAXIMUM, and the
   browser is free to render FEWER columns than that whenever doing so
   produces more even column heights. Director reported "Adjustable Apparel"
   (only 6 products, columns_desktop=5) rendering as 3 columns of 2 items
   instead of 5: confirmed live via getComputedStyle -- `column-count` read
   "5" but only 3 distinct left-offsets existed, because 6 equal-height items
   split perfectly evenly into 3 columns of 2, which balances better than any
   5-column split (which would be four 1-item columns + one 2-item column,
   very uneven). Tested column-fill:auto as a fix: without an explicit
   container height it collapses further, to a SINGLE column (per spec,
   'auto' fill with unconstrained height first computes height as if all
   content were in one column, so nothing ever overflows into column 2).
   Neither CSS knob reliably forces an exact column count for small,
   roughly-equal-height item sets -- this is a real, unfixable-in-pure-CSS
   limitation of the multi-column module, not a bug in FIX-55's math.

   THE FIX: replace CSS columns with a small JS masonry (the same technique
   Pinterest/Isotope-style grids use): build exactly N real DOM column
   containers (flex children of the grid), and assign each product card to
   whichever column currently has the least accumulated height ("shortest
   column first"). This guarantees exactly N columns are always rendered
   regardless of item count (a 6-item / 5-column grid becomes 4 columns of 1
   item + 1 column of 2, all 5 visible), while still packing tall/short cards
   Pinterest-style (a short card's column keeps filling while a tall card's
   column is still busy) -- the exact masonry behavviour FIX-55 intended.

   SCOPE: only `#ProductGridContainer .grid.product-grid` (collection pages
   and search results -- confirmed by reading both section .liquid files,
   same scoping FIX-54/55 used). `sections/related-products.liquid`'s "You
   may also like" carousel has no `#ProductGridContainer` ancestor and is
   untouched, same as before.

   COLUMN COUNT: mirrors FIX-55's exact breakpoints (read from the section's
   own `grid--{N}-col-desktop` / `grid--{N}-col-tablet-down` classes that
   Liquid already renders from the columns_desktop/columns_mobile settings):
     <=650px            -> columns_mobile setting (1 or 2)
     651px-989px        -> columns_mobile==2 renders 3 (base.css's existing
                            tablet enhancement, unchanged from FIX-55)
     990px-1269px       -> 4 (existing implicit mid-desktop tier, unchanged)
     >=1270px           -> columns_desktop setting (1-6)

   LIST VIEW: assets/global.js's #col_1/#col_2 handlers add/remove
   `.list-group-item` on every `.grid__item` (unchanged, not touched here).
   This script watches for that class (a lightweight click listener on
   #col_1/#col_2, same elements global.js binds) and forces a single column
   whenever any item carries `.list-group-item`, matching FIX-55's old
   `:has()` CSS safeguard but now driven by the same JS that builds the
   columns, since a real column rebuild is needed (not just a CSS override)
   when going from N columns back to 1.

   RE-RUN TRIGGERS: initial page load, window resize (debounced, only
   rebuilds if the target column count actually changed), the col_1/col_2
   list-view toggle, and a MutationObserver on #ProductGridContainer's direct
   children -- assets/facets.js's filter/sort AJAX flow does
   `document.getElementById('ProductGridContainer').innerHTML = ...`
   (a full section replace) which drops in a fresh, unwrapped <ul>; the
   observer re-runs masonry on that fresh markup. Observer uses
   `subtree:false` so it only fires on that outer replace, never on this
   script's own nested column-wrapper insertions (which would otherwise
   self-trigger an infinite loop). */
(function () {
  'use strict';
  var GRID_SELECTOR = '#ProductGridContainer .grid.product-grid';
  var COL_GAP_DESKTOP_VAR = '--grid-desktop-horizontal-spacing';
  var COL_GAP_MOBILE_VAR = '--grid-mobile-horizontal-spacing';
  var ROW_GAP_DESKTOP_VAR = '--grid-desktop-vertical-spacing';
  var ROW_GAP_MOBILE_VAR = '--grid-mobile-vertical-spacing';

  function readVar(el, name, fallback) {
    var v = getComputedStyle(el).getPropertyValue(name);
    v = v && v.trim();
    if (!v) return fallback;
    var n = parseFloat(v);
    return isNaN(n) ? fallback : n;
  }

  function targetColumnCount(ul) {
    var desktopMatch = ul.className.match(/grid--(\d)-col-desktop/);
    var mobileMatch = ul.className.match(/grid--(\d)-col-tablet-down/);
    var desktopN = desktopMatch ? parseInt(desktopMatch[1], 10) : 5;
    var mobileN = mobileMatch ? parseInt(mobileMatch[1], 10) : 2;
    var w = window.innerWidth;
    if (w <= 650) return Math.max(1, mobileN);
    if (w <= 989) return mobileN === 2 ? 3 : Math.max(1, mobileN);
    if (w <= 1269) return 4;
    return Math.max(1, desktopN);
  }

  function collectItems(ul) {
    if (!ul._mcItems) {
      var existingCols = ul.querySelectorAll(':scope > .mc-masonry-col');
      if (existingCols.length) {
        var collected = [];
        existingCols.forEach(function (col) {
          Array.prototype.forEach.call(col.children, function (child) {
            collected.push(child);
          });
        });
        ul._mcItems = collected;
      } else {
        ul._mcItems = Array.prototype.slice.call(ul.querySelectorAll(':scope > .grid__item'));
      }
    }
    return ul._mcItems;
  }

  function buildColumns(ul, n) {
    var items = collectItems(ul);
    if (!items.length) return;

    var isMobile = window.innerWidth <= 989;
    var colGap = readVar(ul, isMobile ? COL_GAP_MOBILE_VAR : COL_GAP_DESKTOP_VAR, isMobile ? 16 : 32);
    var rowGap = readVar(ul, isMobile ? ROW_GAP_MOBILE_VAR : ROW_GAP_DESKTOP_VAR, isMobile ? 15 : 30);

    var hasListView = items.some(function (el) {
      return el.classList.contains('list-group-item');
    });
    var effectiveN = hasListView ? 1 : n;

    ul.textContent = '';
    ul.classList.add('mc-masonry-js');
    ul.style.setProperty('--mc-col-gap', colGap + 'px');
    ul.style.setProperty('--mc-row-gap', rowGap + 'px');

    var cols = [];
    var heights = [];
    for (var i = 0; i < effectiveN; i++) {
      var col = document.createElement('div');
      col.className = 'mc-masonry-col';
      ul.appendChild(col);
      cols.push(col);
      heights.push(0);
    }

    items.forEach(function (item) {
      var minIdx = 0;
      for (var j = 1; j < heights.length; j++) {
        if (heights[j] < heights[minIdx]) minIdx = j;
      }
      cols[minIdx].appendChild(item);
      heights[minIdx] += item.getBoundingClientRect().height + rowGap;
    });

    ul._mcColumnCount = effectiveN;
  }

  function reflow(ul, force) {
    var n = targetColumnCount(ul);
    var hasListView = collectItems(ul).some(function (el) {
      return el.classList.contains('list-group-item');
    });
    var effectiveN = hasListView ? 1 : n;
    if (!force && ul._mcColumnCount === effectiveN) return;
    buildColumns(ul, n);
  }

  function reflowAll(force) {
    document.querySelectorAll(GRID_SELECTOR).forEach(function (ul) {
      reflow(ul, force);
    });
  }

  function debounce(fn, wait) {
    var t;
    return function () {
      clearTimeout(t);
      t = setTimeout(fn, wait);
    };
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { reflowAll(true); });
  } else {
    reflowAll(true);
  }

  window.addEventListener('resize', debounce(function () { reflowAll(false); }, 150));

  /* LIST-VIEW TOGGLE (added 2026-08-26, same fix round as the masonry
     rework above): Director reported the list-view icon (the rightmost of
     the two icons next to "Sort by") did nothing when clicked -- confirmed
     live: assets/global.js DOES define a `$("#col_1").click(...)` /
     `$("#col_2").click(...)` binding that adds/removes `.list-group-item`
     on every `.grid__item` (this is what component-card.css's
     `.list-group-item` rules style as a full-width horizontal row with
     description text), and manually re-executing that exact code fetched
     fresh from the live file works perfectly with zero errors -- but on a
     real, fresh, unmodified page load the binding silently never takes
     effect (col_1's click produces no class change, no console error
     either). Same silent-no-op behaviour reproduced independently on
     `.top_button` (the scroll-to-top button), which is bound in the exact
     same top-level statement chain in global.js, right next to the col_1/
     col_2 binding -- confirming this is a real, pre-existing site bug in
     how/when that part of global.js's ready-handler chain executes on a
     first page load, not something specific to col_1/col_2 or something
     this round's masonry rework touched. Root cause not fully isolated
     (no thrown error to catch; re-running the identical fetched source
     later in the same session works every time) -- rather than leave the
     Director's reported bug unfixed while that gets tracked down, this
     performs the exact same class toggle directly, redundantly-safely (if
     global.js's own handler DOES also fire on some page/browser, both do
     the same idempotent class add/remove, no conflict). */
  document.addEventListener('click', function (event) {
    var toggle = event.target.closest('#col_1, #col_2');
    if (!toggle) return;
    event.preventDefault();
    var isListView = toggle.id === 'col_1';
    document.querySelectorAll('.grid__item').forEach(function (item) {
      item.classList.toggle('list-group-item', isListView);
    });
    setTimeout(function () { reflowAll(true); }, 0);
  }, true);

  var observerTargets = new WeakSet();
  function armObserver(container) {
    if (observerTargets.has(container)) return;
    observerTargets.add(container);
    var mo = new MutationObserver(function () {
      reflowAll(true);
    });
    mo.observe(container, { childList: true, subtree: false });
  }
  document.querySelectorAll('#ProductGridContainer').forEach(armObserver);
  document.addEventListener('DOMContentLoaded', function () {
    document.querySelectorAll('#ProductGridContainer').forEach(armObserver);
  });
})();
